/**
 * PayPal JS SDK loader (sc-6943 — in-page checkout).
 *
 * Lazy-loads https://www.paypal.com/sdk/js so the buyer approves a purchase in
 * PayPal's own in-page overlay instead of a full-page redirect away from the
 * cabinet. The client-id is public (VITE_PAYPAL_CLIENT_ID) and MUST match the
 * API's PAYPAL_CLIENT_ID env so the SDK and our server hit the same PayPal app.
 *
 * PayPal needs DIFFERENT SDK configs per product:
 *   - day pass (Orders v2)   → intent=capture
 *   - week/month (Subscription) → vault=true & intent=subscription
 * These can't coexist in one SDK instance, so we load per-intent and cache each
 * separately. A page rarely needs both in one session; if it does, the second
 * load swaps the <script> (PayPal supports re-init with a data-namespace, but
 * for our simple flow a per-intent cached promise is enough).
 *
 * External script → a CSP allow-list entry for https://www.paypal.com is
 * required once a cabinet CSP lands (same class as the ad-network SDKs).
 */

export type PayPalIntent = 'capture' | 'subscription';

/** The subset of the PayPal SDK namespace we use. */
export interface PayPalNamespace {
  Buttons: (config: unknown) => {
    render: (container: HTMLElement) => Promise<void>;
    close?: () => void;
  };
}

const CLIENT_ID = (import.meta.env?.VITE_PAYPAL_CLIENT_ID as string | undefined) ?? '';
const CURRENCY = 'USD';

/** One in-flight/settled load promise per intent (capture vs subscription). */
const loaders = new Map<PayPalIntent, Promise<PayPalNamespace | null>>();

function sdkUrl(intent: PayPalIntent): string {
  const params = new URLSearchParams({ 'client-id': CLIENT_ID, currency: CURRENCY });
  if (intent === 'subscription') {
    params.set('vault', 'true');
    params.set('intent', 'subscription');
  } else {
    params.set('intent', 'capture');
  }
  return `https://www.paypal.com/sdk/js?${params.toString()}`;
}

/**
 * Resolve the PayPal SDK namespace for `intent`, injecting the script once.
 * Resolves `null` (never rejects) when the SDK can't load — no client-id, a
 * blocked/failed script — so the caller can fall back to the redirect flow.
 */
export function loadPayPalSdk(intent: PayPalIntent): Promise<PayPalNamespace | null> {
  if (!CLIENT_ID) return Promise.resolve(null);
  const cached = loaders.get(intent);
  if (cached) return cached;

  const promise = new Promise<PayPalNamespace | null>((resolve) => {
    const script = document.createElement('script');
    script.src = sdkUrl(intent);
    // data-namespace keeps intents from clobbering window.paypal if both load.
    const ns = `paypal_${intent}`;
    script.setAttribute('data-namespace', ns);
    script.async = true;
    script.onload = () => {
      const w = window as unknown as Record<string, PayPalNamespace | undefined>;
      resolve(w[ns] ?? w['paypal'] ?? null);
    };
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  loaders.set(intent, promise);
  return promise;
}

/** True when a client-id is configured (the modal path is even possible). */
export function isPayPalSdkConfigured(): boolean {
  return CLIENT_ID.length > 0;
}
